import { useCallback, useState, useEffect, useContext, useRef } from "react";
import { MapContext } from "../../providers/MapProvider";
import {
  FlightHop,
  MapLocation,
  MapItinerary,
  TripLegMode,
} from "types/map";
import {
  hasLatLong,
  mapDirectionResultParser,
  toGoogleLatLong,
  buildItineraryRouteKey,
} from "libraries/map";
import {
  flightDistanceMeters,
  flightEmissionKg,
  greatCircleDistanceKm,
  toLatLng,
} from "libraries/flight";
import {
  avoidedCarEmissionKg,
  trainDistanceKm,
  TRAIN_KG_CO2_PER_KM,
} from "libraries/legEmissions";
import { padTripLegModes } from "libraries/tripLeg";
import { useAppSelector, useAppDispatch } from "redux/hooks";
import {
  selectReferenceTripLegModes,
  selectReferenceCar,
  selectReferenceElectricGridCountry,
  registerReferenceItinerary,
} from "redux/slices/shed/shedSlice";

type DirectionService = google.maps.DirectionsService;

type Props = {
  from: MapLocation;
  destinations: MapLocation[];
};

type ReturnType = MapItinerary | null;

type Hop = {
  from: MapLocation;
  to: MapLocation;
  mode: TripLegMode;
};

type RouteRun = {
  hops: Hop[];
  mode: TripLegMode;
};

const googleModeForLeg = (
  mode: TripLegMode
): google.maps.TravelMode | null => {
  if (mode === "drive") {
    return google.maps.TravelMode.DRIVING;
  }
  if (mode === "walk") {
    return google.maps.TravelMode.WALKING;
  }
  if (mode === "bike") {
    return google.maps.TravelMode.BICYCLING;
  }
  if (mode === "train") {
    return google.maps.TravelMode.TRANSIT;
  }
  return null;
};

const groupSameModeRuns = (hops: Hop[]): RouteRun[] => {
  const runs: RouteRun[] = [];

  hops.forEach((hop) => {
    const last = runs[runs.length - 1];
    if (last && last.mode === hop.mode) {
      last.hops.push(hop);
      return;
    }
    runs.push({ hops: [hop], mode: hop.mode });
  });

  return runs;
};

const unionBounds = (
  boundsList: Array<google.maps.LatLngBounds | null | undefined>,
  flightHops: FlightHop[],
  stopPoints: MapLocation[]
): google.maps.LatLngBounds | null => {
  const bounds = new google.maps.LatLngBounds();
  let hasPoint = false;

  boundsList.forEach((item) => {
    if (!item) {
      return;
    }
    bounds.union(item);
    hasPoint = true;
  });

  flightHops.forEach((hop) => {
    bounds.extend(hop.from);
    bounds.extend(hop.to);
    hasPoint = true;
  });

  // Always include every trip pin so failed routes / fly-only legs still fit.
  stopPoints.forEach((point) => {
    if (!hasLatLong(point)) {
      return;
    }
    bounds.extend({
      lat: point.latitude as number,
      lng: point.longitude as number,
    });
    hasPoint = true;
  });

  return hasPoint ? bounds : null;
};

const useDirections = ({ from, destinations }: Props): ReturnType => {
  const dispatch = useAppDispatch();
  const { initialized, mapsLibraries } = useContext(MapContext);
  const storedLegModes = useAppSelector(selectReferenceTripLegModes);
  const referenceCar = useAppSelector(selectReferenceCar);
  const gridCountry = useAppSelector(selectReferenceElectricGridCountry);
  const lastRouteKeyRef = useRef("");

  const [directionService, setDirectionService] =
    useState<DirectionService | null>(null);

  const filledDestinations = destinations.filter(hasLatLong);
  const legModes = padTripLegModes(storedLegModes, filledDestinations.length);
  const routeKey = buildItineraryRouteKey(
    from,
    destinations,
    "PER_LEG",
    legModes
  );

  const saveDirectionToStore = useCallback(
    (itinerary: MapItinerary) =>
      dispatch(registerReferenceItinerary(itinerary)),
    [dispatch]
  );

  useEffect(() => {
    initialized &&
      mapsLibraries &&
      setDirectionService(new mapsLibraries.DirectionsService());
  }, [initialized, mapsLibraries]);

  useEffect(() => {
    if (!directionService) {
      return;
    }

    const finalDestination = filledDestinations[filledDestinations.length - 1];

    if (
      !hasLatLong(from) ||
      !finalDestination ||
      !hasLatLong(finalDestination)
    ) {
      lastRouteKeyRef.current = "";
      return;
    }

    if (routeKey === lastRouteKeyRef.current) {
      return;
    }

    lastRouteKeyRef.current = routeKey;

    const points = [from, ...filledDestinations];
    const hops: Hop[] = filledDestinations.map((to, index) => ({
      from: points[index],
      to,
      mode: legModes[index] ?? "drive",
    }));

    const flightHops: FlightHop[] = hops
      .filter((hop) => hop.mode === "fly")
      .map((hop) => {
        const origin = toLatLng(hop.from);
        const destination = toLatLng(hop.to);
        if (!origin || !destination) {
          return null;
        }
        return { from: origin, to: destination };
      })
      .filter((hop): hop is FlightHop => hop !== null);

    const flightDistanceTotal = flightHops.reduce(
      (total, hop) => total + flightDistanceMeters(hop.from, hop.to),
      0
    );
    const flightKgTotal = flightHops.reduce(
      (total, hop) => total + flightEmissionKg(hop.from, hop.to),
      0
    );

    const withLocations = (
      parsed: Omit<MapItinerary, "locationFrom" | "locationTo">
    ): MapItinerary => ({
      ...parsed,
      locationFrom: from,
      locationTo: finalDestination,
      destinations,
      waypoints: destinations.slice(0, -1),
      flightHops,
      flightDistanceMeters: flightDistanceTotal,
      flightKgEmission: flightKgTotal,
    });

    const requestRun = (run: RouteRun) =>
      new Promise<{
        result: google.maps.DirectionsResult | null;
        meters: number;
        duration: number;
        bounds: google.maps.LatLngBounds | null;
        mode: TripLegMode;
      }>((resolve) => {
        const googleMode = googleModeForLeg(run.mode);
        if (!googleMode) {
          resolve({
            result: null,
            meters: 0,
            duration: 0,
            bounds: null,
            mode: run.mode,
          });
          return;
        }

        const origin = run.hops[0].from;
        const destination = run.hops[run.hops.length - 1].to;
        const waypoints = run.hops.slice(0, -1).map((hop) => hop.to);

        directionService.route(
          {
            origin: toGoogleLatLong(origin.latitude, origin.longitude),
            destination: toGoogleLatLong(
              destination.latitude,
              destination.longitude
            ),
            waypoints: waypoints.map((stop) => ({
              location: toGoogleLatLong(stop.latitude, stop.longitude),
              stopover: true,
            })),
            optimizeWaypoints: false,
            travelMode: googleMode,
          },
          (result, status) => {
            if (status === "OK" && result) {
              const parsed = mapDirectionResultParser(result);
              resolve({
                result,
                meters: parsed.distanceMeters,
                duration: parsed.durationSeconds,
                bounds: parsed.bounds,
                mode: run.mode,
              });
              return;
            }

            const meters = run.hops.reduce((total, hop) => {
              const fromLatLng = toLatLng(hop.from);
              const toLatLngValue = toLatLng(hop.to);
              if (!fromLatLng || !toLatLngValue) {
                return total;
              }
              if (hop.mode === "train") {
                return total + trainDistanceKm(fromLatLng, toLatLngValue) * 1000;
              }
              return (
                total + greatCircleDistanceKm(fromLatLng, toLatLngValue) * 1000
              );
            }, 0);

            resolve({
              result: null,
              meters,
              duration: 0,
              bounds: null,
              mode: run.mode,
            });
          }
        );
      });

    const runs = groupSameModeRuns(hops);
    const routedRuns = runs.filter((run) => googleModeForLeg(run.mode));

    const finish = (
      routed: Array<{
        result: google.maps.DirectionsResult | null;
        meters: number;
        duration: number;
        bounds: google.maps.LatLngBounds | null;
        mode: TripLegMode;
      }>
    ) => {
      const directionResults = routed
        .map((item) => item.result)
        .filter((item): item is google.maps.DirectionsResult => Boolean(item));

      const driveMeters = routed
        .filter((item) => item.mode === "drive")
        .reduce((total, item) => total + item.meters, 0);
      const walkMeters = routed
        .filter((item) => item.mode === "walk")
        .reduce((total, item) => total + item.meters, 0);
      const cycleMeters = routed
        .filter((item) => item.mode === "bike")
        .reduce((total, item) => total + item.meters, 0);
      const trainMeters = routed
        .filter((item) => item.mode === "train")
        .reduce((total, item) => total + item.meters, 0);
      const durationSeconds = routed.reduce(
        (total, item) => total + item.duration,
        0
      );

      const walkBikeAvoidedKg = avoidedCarEmissionKg(
        walkMeters + cycleMeters,
        referenceCar,
        gridCountry
      );

      const trainKg = routed
        .filter((item) => item.mode === "train")
        .reduce(
          (total, item) =>
            total + (item.meters / 1000) * TRAIN_KG_CO2_PER_KM,
          0
        );

      const distanceLabel =
        driveMeters >= 1000
          ? `${Math.round(driveMeters / 1000)} km`
          : driveMeters > 0
            ? `${driveMeters} m`
            : "";

      saveDirectionToStore(
        withLocations({
          distanceMeters: driveMeters,
          distanceLabel,
          durationSeconds,
          googleDirection: directionResults[0] ?? null,
          googleDirections: directionResults.slice(1),
          bounds: unionBounds(
            routed.map((item) => item.bounds),
            flightHops,
            points
          ),
          walkDistanceMeters: walkMeters,
          cycleDistanceMeters: cycleMeters,
          trainDistanceMeters: trainMeters,
          trainKgEmission: trainKg,
          walkBikeAvoidedKgEmission: walkBikeAvoidedKg,
        })
      );
    };

    if (routedRuns.length === 0) {
      finish([]);
      return;
    }

    Promise.all(routedRuns.map(requestRun)).then(finish);
  }, [
    destinations,
    directionService,
    filledDestinations,
    from,
    gridCountry,
    legModes,
    referenceCar,
    routeKey,
    saveDirectionToStore,
  ]);

  return null;
};

export default useDirections;
